<template>
  <div class="custom-fields-popup">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        data-cy="custom-fields-dialog"
        class="flex max-h-[90vh] w-[920px] max-w-[95vw] flex-col gap-0 overflow-hidden p-0 !top-[5vh] !translate-y-0"
        @open-auto-focus.prevent
        @pointer-down-outside.prevent
        @focus-outside.prevent
      >
        <SDialogHeader class="shrink-0 border-b px-6 py-4 text-left">
          <SDialogTitle class="text-lg font-semibold">
            Custom fields
          </SDialogTitle>
        </SDialogHeader>
        <div class="flex min-h-0 flex-1 flex-col">
          <div
            v-if="
              bookingTypeCustomFields.length || ticketTypeCustomFields.length
            "
            class="flex shrink-0 border-b"
          >
            <button
              type="button"
              v-if="bookingTypeCustomFields.length"
              @click="selectBookingTypeCustomFields"
              data-cy="custom-fields-booking-tab"
              class="-mb-px flex-1 cursor-pointer border-b-2 py-3 text-center text-sm font-medium transition"
              :class="
                displayedCustomFieldsType === CustomFieldType.BOOKING
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              "
            >
              {{ $t("custom-fields.booking") }}
            </button>
            <button
              type="button"
              v-if="ticketTypeCustomFields.length"
              @click="selectTicketTypeCustomFields"
              data-cy="custom-fields-tickets-tab"
              class="-mb-px flex-1 cursor-pointer border-b-2 py-3 text-center text-sm font-medium transition"
              :class="
                displayedCustomFieldsType === CustomFieldType.TICKET
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              "
            >
              {{ $t("custom-fields.individual-tickets") }}
            </button>
          </div>

          <div class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <div class="mx-auto max-w-[560px]">
              <!-- CUSTOM FIELDS OF TYPE BOOKING -->
              <div
                v-if="
                  computedDisplayedCustomFieldsType === CustomFieldType.BOOKING
                "
                class="space-y-6"
              >
                <div
                  v-for="cust in bookingTypeCustomFields"
                  :key="cust.uuid"
                  data-cy="cf-field"
                  :data-cy-field="cust.customField.name"
                >
                  <div>
                    <label
                      class="mb-1.5 block text-sm font-medium text-foreground"
                    >
                      {{ cust.customField.name }}
                      <span
                        v-if="
                          cust.customField.validation ===
                          CustomFieldValidation.MANDATORY
                        "
                        class="text-destructive"
                      >
                        *
                      </span>
                    </label>

                    <!-- phone number -->
                    <div
                      v-if="
                        cust.customField.informationType ===
                        InformationType.PHONE
                      "
                    >
                      <MazPhoneNumberInput
                        data-cy="cf-phone"
                        v-model="cust.answer"
                        color="info"
                        show-code-on-list
                        :default-country-code="
                          getCountryCodeFromPhone(cust) || userCurrentCountry
                        "
                        list-position="top left"
                        @update="results = $event"
                        :success="results?.isValid"
                        size="md"
                      />
                    </div>
                    <!-- phone number -->

                    <!-- File, Input, Textarea, Country selector, Select, Calendar -->
                    <div
                      v-else-if="
                        cust.customField.questionType !== QuestionType.LIST &&
                        cust.customField.questionType !== QuestionType.RADIO
                      "
                    >
                      <!-- File -->
                      <div
                        v-if="
                          cust.customField.questionType === QuestionType.FILE
                        "
                      >
                        <div
                          class="relative w-fit"
                          v-if="
                            !cust.fileUrl /*No file yet, we show the input*/ ||
                            cust.isCropped /*there is cropped file we preview it*/
                          "
                        >
                          <label class="block cursor-pointer">
                            <input
                              v-if="!cust.fileUrl"
                              class="hidden"
                              :id="cust.uuid"
                              data-cy="file-input"
                              type="file"
                              name="resume"
                              :ref="cust.uuid"
                              accept="image/*"
                              @change="
                                handleFileUpload(
                                  $event,
                                  cust.uuid,
                                  CustomFieldType.BOOKING
                                )
                              "
                            />
                            <div
                              v-if="!cust.fileUrl"
                              data-cy="upload-button"
                              class="flex h-[132px] w-[240px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-input bg-muted/30 text-muted-foreground transition hover:border-ring hover:bg-muted/50"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.8"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              >
                                <path
                                  d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                                />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" x2="12" y1="3" y2="15" />
                              </svg>
                              <span class="text-sm">Upload file</span>
                            </div>
                            <div
                              v-if="cust.fileUrl && cust.isCropped"
                              class="overflow-hidden rounded-lg border border-input"
                            >
                              <img
                                class="h-[132px] w-[132px] object-cover"
                                :src="cust.fileUrl"
                                alt="File uploaded"
                              />
                            </div>
                          </label>
                          <span
                            v-if="cust.isInvalid"
                            class="mt-1 block text-sm text-destructive"
                            >{{ cust.validationMessage }}</span
                          >
                          <button
                            v-if="cust.fileUrl"
                            type="button"
                            @click="removeFile(cust)"
                            class="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full border bg-background shadow-sm transition hover:bg-muted"
                          >
                            <SvgIcon
                              :iconContent="binIcon"
                              :iconAlt="binAlt"
                              :iconStyle="binIconStyle"
                            />
                          </button>
                        </div>
                        <div
                          v-if="
                            cust.fileUrl &&
                            (cust.customField.informationType ===
                              InformationType.PASSPORT ||
                              cust.customField.informationType ===
                                InformationType.PICTURE) &&
                            !cust.isCropped
                          "
                          style="
                            margin-bottom: 16px;
                            display: flex;
                            flex-direction: column;
                            gap: 12px;
                            width: fit-content;
                            max-width: 100%;
                          "
                          :ref="cust.uuid + '_cropper_parent'"
                        >
                          <div
                            :style="{
                              width:
                                cust.customField.informationType ===
                                InformationType.PASSPORT
                                  ? '280px'
                                  : '320px',
                              height:
                                cust.customField.informationType ===
                                InformationType.PASSPORT
                                  ? '360px'
                                  : '320px',
                              maxWidth: '100%',
                              maxHeight: '60vh',
                            }"
                          >
                            <SmeetzCropper
                              data-cy="image-cropper"
                              v-if="cust.fileUrl"
                              :ref="cust.uuid + '_cropper'"
                              :src="cust.fileUrl"
                              :aspect-ratio="
                                cust.customField.informationType ===
                                InformationType.PASSPORT
                                  ? 3.5 / 4.5
                                  : 1
                              "
                              :view-mode="1"
                            />
                          </div>
                          <div
                            class="d-flex confirm-button-container"
                            style="margin-top: 0px"
                          >
                            <SButton
                              type="button"
                              variant="destructive"
                              @click="cancelCroppedPicture(cust)"
                              data-cy="cropper-remove-button"
                            >
                              {{ $t("button.remove") }}
                            </SButton>
                            <SButton
                              type="button"
                              @click="confirmCroppedPicture(cust)"
                              data-cy="cropper-confirm-button"
                            >
                              {{ $t("button.save") }}
                            </SButton>
                          </div>
                        </div>
                      </div>
                      <!-- File -->

                      <!-- Input -->
                      <SInput
                        data-cy="cf-text"
                        v-if="
                          cust.customField.questionType === QuestionType.TEXT &&
                          cust.customField.informationType !==
                            InformationType.COUNTRY &&
                          cust.customField.informationType !==
                            InformationType.EMAIL
                        "
                        class="w-full"
                        :placeholder="cust.customField.answerExample || ''"
                        v-model="cust.answer"
                      />
                      <SInput
                        data-cy="cf-text"
                        v-if="
                          cust.customField.questionType === QuestionType.TEXT &&
                          cust.customField.informationType !==
                            InformationType.COUNTRY &&
                          cust.customField.informationType ===
                            InformationType.EMAIL
                        "
                        class="w-full"
                        :placeholder="cust.customField.answerExample || ''"
                        v-model="cust.answer"
                        :aria-invalid="
                          (cust.answer &&
                            isEmailValid(cust).isValid === false) ||
                          undefined
                        "
                      />
                      <span
                        class="mt-1 block text-sm text-destructive"
                        data-cy="cf-invalid-email"
                        v-if="
                          cust.answer && isEmailValid(cust).isValid === false
                        "
                      >
                        {{ $t("custom-fields.invalid-email") }}
                      </span>
                      <!-- Input -->

                      <!-- Textarea -->
                      <STextarea
                        data-cy="cf-textarea"
                        v-if="
                          cust.customField.questionType ===
                          QuestionType.LONG_TEXT
                        "
                        v-model="cust.answer"
                        :rows="2"
                        class="w-full"
                        :placeholder="cust.customField.answerExample || ''"
                      />
                      <!-- Textarea -->

                      <!-- COUNTRY Selector -->
                      <div
                        v-else-if="
                          cust.customField.informationType ===
                          InformationType.COUNTRY
                        "
                        data-cy="cf-country"
                      >
                        <SCountrySelect
                          v-model="cust.answer"
                          :lang="appLanguage"
                          :priority-country-codes="[
                            'DE',
                            'US',
                            'GB',
                            'FR',
                            'IT',
                          ]"
                          :priority-label="
                            $t('custom-fields.frequently-selected')
                          "
                          :countries-label="$t('custom-fields.all-countries')"
                          :placeholder="$t('custom-fields.select-country')"
                        />
                      </div>
                      <!-- COUNTRY Selector -->

                      <!-- LANGUAGE Selector -->
                      <SSelect
                        v-else-if="
                          cust.customField.informationType ===
                          InformationType.LANGUAGE
                        "
                        v-model="cust.answer"
                      >
                        <SSelectTrigger class="w-full" data-cy="cf-select">
                          <SSelectValue :placeholder="$t('button.select')" />
                        </SSelectTrigger>
                        <SSelectContent>
                          <SSelectItem
                            v-for="item in languagesList(cust.customField.list)"
                            :key="item.key"
                            :value="item.key"
                          >
                            {{ item.value }}
                          </SSelectItem>
                        </SSelectContent>
                      </SSelect>
                      <!-- LANGUAGE Selector -->

                      <!-- Select -->
                      <SSelect
                        v-else-if="
                          cust.customField.questionType ===
                          QuestionType.DROPDOWN
                        "
                        v-model="cust.answer"
                      >
                        <SSelectTrigger class="w-full" data-cy="cf-select">
                          <SSelectValue :placeholder="$t('button.select')" />
                        </SSelectTrigger>
                        <SSelectContent>
                          <SSelectItem
                            v-for="item in cust.customField.list"
                            :key="item"
                            :value="item"
                          >
                            {{ item }}
                          </SSelectItem>
                        </SSelectContent>
                      </SSelect>
                      <!-- Selector -->

                      <!-- Calendar -->
                      <div
                        v-else-if="
                          cust.customField.questionType === QuestionType.DATE
                        "
                        data-cy="cf-date"
                      >
                        <SDatePicker
                          :model-value="dateAnswerValue(cust.answer)"
                          display-format="YYYY-MM-DD"
                          trigger-class="w-full"
                          :placeholder="$t('customer.birthdate-placeholder')"
                          @update:model-value="
                            (value) =>
                              (cust.answer = value ? value.toString() : '')
                          "
                        />
                      </div>
                      <!-- Calendar -->
                    </div>
                    <!-- File, Input, Textarea, Country selector, Select, Calendar -->

                    <!-- unique checkbox radio -->
                    <div
                      v-else-if="
                        cust.customField.questionType === QuestionType.RADIO
                      "
                    >
                      <SRadioGroup
                        data-cy="cf-radio"
                        v-model="cust.answer"
                        class="flex flex-col gap-2"
                      >
                        <div
                          v-for="(item, index) in cust.customField.list"
                          :key="item"
                          class="flex items-center gap-2"
                        >
                          <SRadioGroupItem
                            :data-cy-option="stripHtml(item)"
                            :id="`radio-${cust.uuid}-${index}`"
                            :value="item"
                          />
                          <SLabel :for="`radio-${cust.uuid}-${index}`">
                            {{ item }}
                          </SLabel>
                        </div>
                      </SRadioGroup>
                    </div>
                    <!-- unique checkbox radio -->

                    <!-- list of checkbox -->
                    <div
                      v-else-if="
                        cust.customField.questionType === QuestionType.LIST
                      "
                    >
                      <div data-cy="cf-list" class="flex flex-col gap-2">
                        <label
                          v-for="item in cust.customField.list"
                          :key="item"
                          class="flex cursor-pointer items-center gap-2"
                        >
                          <SCheckbox
                            :data-cy-option="stripHtml(item)"
                            :model-value="isListChecked(cust, item)"
                            @update:model-value="
                              (checked) => toggleListAnswer(cust, item, checked)
                            "
                          />
                          <span>{{ stripHtml(item) }}</span>
                        </label>
                      </div>
                    </div>
                    <!-- checkbox -->
                    <span
                      class="mt-1 block text-sm text-destructive"
                      data-cy="cf-required-error"
                      v-if="
                        !cust.answer &&
                        cust.customField.validation ===
                          CustomFieldValidation.MANDATORY &&
                        canValidate
                      "
                    >
                      {{ $t("custom-fields.required-field") }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- CUSTOM FIELDS OF TYPE TICKETS -->
              <div
                v-if="
                  computedDisplayedCustomFieldsType === CustomFieldType.TICKET
                "
                class="space-y-6"
              >
                <div
                  v-if="priceCategoryData.length > 1"
                  class="flex flex-wrap gap-2"
                >
                  <button
                    v-for="priceCategory in priceCategoryData"
                    :key="
                      priceCategory.categoryId + '_' + priceCategory.priceId
                    "
                    type="button"
                    @click="selectPriceCategory(priceCategory)"
                    class="cursor-pointer rounded-full border px-3 py-1.5 text-sm transition"
                    :class="
                      selectedPriceCategory.priceId === priceCategory.priceId &&
                      selectedPriceCategory.categoryId ===
                        priceCategory.categoryId
                        ? 'border-primary bg-primary/10 font-medium text-foreground'
                        : 'border-input text-muted-foreground hover:bg-muted/60'
                    "
                  >
                    {{
                      priceCategory.categoryName + " " + priceCategory.priceName
                    }}
                  </button>
                </div>
                <div
                  v-for="(
                    groupedFields, index
                  ) in groupedTicketTypeCustomFieldsByPSS.filter(
                    (groupedF) =>
                      groupedF.customFields.some(
                        (cust) =>
                          cust.priceId === selectedPriceCategory.priceId &&
                          cust.categoryId === selectedPriceCategory.categoryId
                      ) === true
                  )"
                  :key="groupedFields.pssUuid"
                  data-cy="cf-ticket-group"
                  :data-cy-ticket="index + 1"
                  class="space-y-5"
                  :class="{ 'border-t border-border pt-6': index > 0 }"
                >
                  <p class="text-base font-semibold text-foreground">
                    Ticket {{ index + 1 }}
                  </p>
                  <div
                    v-for="cust in groupedFields.customFields"
                    :key="cust.priceId + '_' + cust.categoryId"
                    data-cy="cf-field"
                    :data-cy-field="cust.customField.name"
                  >
                    <div>
                      <label
                        class="mb-1.5 block text-sm font-medium text-foreground"
                      >
                        {{ cust.customField.name
                        }}<span
                          v-if="
                            cust.customField.validation ===
                            CustomFieldValidation.MANDATORY
                          "
                          class="text-destructive"
                        >
                          *
                        </span>
                      </label>

                      <!-- phone number -->
                      <div
                        v-if="
                          cust.customField.informationType ===
                          InformationType.PHONE
                        "
                      >
                        <MazPhoneNumberInput
                          data-cy="cf-phone"
                          v-model="cust.answer"
                          color="info"
                          show-code-on-list
                          :default-country-code="
                            getCountryCodeFromPhone(cust) || userCurrentCountry
                          "
                          list-position="top left"
                          @update="results = $event"
                          :success="results?.isValid"
                          size="md"
                        />
                      </div>
                      <!-- phone number -->

                      <!-- File, Input, Textarea, Country selector, Select, Calendar -->
                      <div
                        v-else-if="
                          cust.customField.questionType !== QuestionType.LIST &&
                          cust.customField.questionType !== QuestionType.RADIO
                        "
                      >
                        <!-- File -->
                        <div
                          v-if="
                            cust.customField.questionType === QuestionType.FILE
                          "
                        >
                          <div
                            class="relative w-fit"
                            v-if="
                              !cust.fileUrl /*No file yet, we show the input*/ ||
                              cust.isCropped /*there is cropped file we preview it*/
                            "
                          >
                            <label class="block cursor-pointer">
                              <input
                                v-if="!cust.fileUrl"
                                class="hidden"
                                :id="cust.uuid"
                                data-cy="file-input"
                                type="file"
                                name="resume"
                                :ref="cust.uuid"
                                accept="image/*"
                                @change="
                                  handleFileUpload(
                                    $event,
                                    cust.uuid,
                                    CustomFieldType.TICKET
                                  )
                                "
                              />
                              <div
                                v-if="!cust.fileUrl"
                                data-cy="upload-button"
                                class="flex h-[132px] w-[240px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-input bg-muted/30 text-muted-foreground transition hover:border-ring hover:bg-muted/50"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="22"
                                  height="22"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="1.8"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                >
                                  <path
                                    d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                                  />
                                  <polyline points="17 8 12 3 7 8" />
                                  <line x1="12" x2="12" y1="3" y2="15" />
                                </svg>
                                <span class="text-sm">Upload file</span>
                              </div>
                              <div
                                v-if="cust.fileUrl && cust.isCropped"
                                class="overflow-hidden rounded-lg border border-input"
                              >
                                <img
                                  class="h-[132px] w-[132px] object-cover"
                                  :src="cust.fileUrl"
                                  alt="File uploaded"
                                />
                              </div>
                            </label>
                            <span
                              v-if="cust.isInvalid"
                              class="mt-1 block text-sm text-destructive"
                              >{{ cust.validationMessage }}</span
                            >
                            <button
                              v-if="cust.fileUrl"
                              type="button"
                              @click="removeFile(cust)"
                              class="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full border bg-background shadow-sm transition hover:bg-muted"
                            >
                              <SvgIcon
                                :iconContent="binIcon"
                                :iconAlt="binAlt"
                                :iconStyle="binIconStyle"
                              />
                            </button>
                          </div>
                          <div
                            v-if="
                              cust.fileUrl &&
                              (cust.customField.informationType ===
                                InformationType.PASSPORT ||
                                cust.customField.informationType ===
                                  InformationType.PICTURE) &&
                              !cust.isCropped
                            "
                            style="
                              margin-bottom: 16px;
                              display: flex;
                              flex-direction: column;
                              gap: 12px;
                              width: fit-content;
                              max-width: 100%;
                            "
                            :ref="cust.uuid + '_cropper_parent'"
                          >
                            <div
                              :style="{
                                width:
                                  cust.customField.informationType ===
                                  InformationType.PASSPORT
                                    ? '280px'
                                    : '320px',
                                height:
                                  cust.customField.informationType ===
                                  InformationType.PASSPORT
                                    ? '360px'
                                    : '320px',
                                maxWidth: '100%',
                                maxHeight: '60vh',
                              }"
                            >
                              <SmeetzCropper
                                data-cy="image-cropper"
                                v-if="cust.fileUrl"
                                :ref="cust.uuid + '_cropper'"
                                :src="cust.fileUrl"
                                :aspect-ratio="
                                  cust.customField.informationType ===
                                  InformationType.PASSPORT
                                    ? 3.5 / 4.5
                                    : 1
                                "
                                :view-mode="1"
                              />
                            </div>
                            <div
                              class="d-flex confirm-button-container"
                              style="margin-top: 0px"
                            >
                              <SButton
                                type="button"
                                variant="destructive"
                                @click="cancelCroppedPicture(cust)"
                                data-cy="cropper-remove-button"
                              >
                                {{ $t("button.remove") }}
                              </SButton>
                              <SButton
                                type="button"
                                data-cy="cropper-confirm-button"
                                @click="confirmCroppedPicture(cust)"
                              >
                                {{ $t("button.save") }}
                              </SButton>
                            </div>
                          </div>
                        </div>
                        <!-- File -->

                        <!-- Input -->
                        <SInput
                          data-cy="cf-text"
                          v-if="
                            cust.customField.questionType ===
                              QuestionType.TEXT &&
                            cust.customField.informationType !==
                              InformationType.COUNTRY &&
                            cust.customField.informationType !==
                              InformationType.EMAIL
                          "
                          class="w-full"
                          :placeholder="cust.customField.answerExample || ''"
                          v-model="cust.answer"
                        />
                        <SInput
                          data-cy="cf-text"
                          v-if="
                            cust.customField.questionType ===
                              QuestionType.TEXT &&
                            cust.customField.informationType !==
                              InformationType.COUNTRY &&
                            cust.customField.informationType ===
                              InformationType.EMAIL
                          "
                          class="w-full"
                          :placeholder="cust.customField.answerExample || ''"
                          v-model="cust.answer"
                          :aria-invalid="
                            (cust.answer &&
                              isEmailValid(cust).isValid === false) ||
                            undefined
                          "
                        />
                        <span
                          class="mt-1 block text-sm text-destructive"
                          data-cy="cf-invalid-email"
                          v-if="
                            cust.answer && isEmailValid(cust).isValid === false
                          "
                        >
                          {{ $t("custom-fields.invalid-email") }}
                        </span>
                        <!-- Input -->

                        <!-- Textarea -->
                        <STextarea
                          data-cy="cf-textarea"
                          v-if="
                            cust.customField.questionType ===
                            QuestionType.LONG_TEXT
                          "
                          v-model="cust.answer"
                          :rows="2"
                          class="w-full"
                          :placeholder="cust.customField.answerExample || ''"
                        />
                        <!-- Textarea -->

                        <!-- COUNTRY Selector -->
                        <div
                          v-else-if="
                            cust.customField.informationType ===
                            InformationType.COUNTRY
                          "
                          data-cy="cf-country"
                        >
                          <SCountrySelect
                            v-model="cust.answer"
                            :lang="appLanguage"
                            :priority-country-codes="[
                              'DE',
                              'US',
                              'GB',
                              'FR',
                              'IT',
                            ]"
                            :priority-label="
                              $t('custom-fields.frequently-selected')
                            "
                            :countries-label="$t('custom-fields.all-countries')"
                            :placeholder="$t('custom-fields.select-country')"
                          />
                        </div>
                        <!-- COUNTRY Selector -->

                        <!-- LANGUAGE Selector -->
                        <SSelect
                          v-else-if="
                            cust.customField.informationType ===
                            InformationType.LANGUAGE
                          "
                          v-model="cust.answer"
                        >
                          <SSelectTrigger class="w-full" data-cy="cf-select">
                            <SSelectValue :placeholder="$t('button.select')" />
                          </SSelectTrigger>
                          <SSelectContent>
                            <SSelectItem
                              v-for="item in languagesList(
                                cust.customField.list
                              )"
                              :key="item.key"
                              :value="item.key"
                            >
                              {{ item.value }}
                            </SSelectItem>
                          </SSelectContent>
                        </SSelect>
                        <!-- LANGUAGE Selector -->

                        <!-- Select -->
                        <SSelect
                          v-else-if="
                            cust.customField.questionType ===
                            QuestionType.DROPDOWN
                          "
                          v-model="cust.answer"
                        >
                          <SSelectTrigger class="w-full" data-cy="cf-select">
                            <SSelectValue :placeholder="$t('button.select')" />
                          </SSelectTrigger>
                          <SSelectContent>
                            <SSelectItem
                              v-for="item in cust.customField.list"
                              :key="item"
                              :value="item"
                            >
                              {{ item }}
                            </SSelectItem>
                          </SSelectContent>
                        </SSelect>
                        <!-- Selector -->

                        <!-- Calendar -->
                        <div
                          v-else-if="
                            cust.customField.questionType === QuestionType.DATE
                          "
                          data-cy="cf-date"
                        >
                          <SDatePicker
                            :model-value="dateAnswerValue(cust.answer)"
                            display-format="YYYY-MM-DD"
                            trigger-class="w-full"
                            :placeholder="$t('customer.birthdate-placeholder')"
                            @update:model-value="
                              (value) =>
                                (cust.answer = value ? value.toString() : '')
                            "
                          />
                        </div>
                        <!-- Calendar -->
                      </div>
                      <!-- File, Input, Textarea, Country selector, Select, Calendar -->

                      <!-- unique checkbox radio -->
                      <div
                        v-else-if="
                          cust.customField.questionType === QuestionType.RADIO
                        "
                      >
                        <SRadioGroup
                          data-cy="cf-radio"
                          v-model="cust.answer"
                          class="flex flex-col gap-2"
                        >
                          <div
                            v-for="(item, index) in cust.customField.list"
                            :key="item"
                            class="flex items-center gap-2"
                          >
                            <SRadioGroupItem
                              :data-cy-option="stripHtml(item)"
                              :id="`radio-${cust.uuid}-${index}`"
                              :value="item"
                            />
                            <SLabel :for="`radio-${cust.uuid}-${index}`">
                              {{ item }}
                            </SLabel>
                          </div>
                        </SRadioGroup>
                      </div>
                      <!-- unique checkbox radio -->

                      <!-- list of checkbox -->
                      <div
                        v-else-if="
                          cust.customField.questionType === QuestionType.LIST
                        "
                      >
                        <div data-cy="cf-list" class="flex flex-col gap-2">
                          <label
                            v-for="item in cust.customField.list"
                            :key="item"
                            class="flex cursor-pointer items-center gap-2"
                          >
                            <SCheckbox
                              :data-cy-option="stripHtml(item)"
                              :model-value="isListChecked(cust, item)"
                              @update:model-value="
                                (checked) =>
                                  toggleListAnswer(cust, item, checked)
                              "
                            />
                            <span>{{ stripHtml(item) }}</span>
                          </label>
                        </div>
                      </div>
                      <!-- checkbox -->
                      <span
                        class="mt-1 block text-sm text-destructive"
                        data-cy="cf-required-error"
                        v-if="
                          !cust.answer &&
                          cust.customField.validation ===
                            CustomFieldValidation.MANDATORY &&
                          canValidate
                        "
                      >
                        {{ $t("custom-fields.required-field") }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SDialogFooter
          class="shrink-0 items-center justify-between gap-3 border-t px-6 py-4"
        >
          <p class="text-sm text-muted-foreground">
            {{ $t("custom-fields.footer-text") }}
          </p>
          <div class="flex justify-end gap-2">
            <SButton
              type="button"
              variant="outline"
              @click="hideDialog"
              data-cy="custom-fields-cancel-button"
            >
              {{ $t("button.cancel") }}
            </SButton>
            <SButton
              type="button"
              :loading="isLoading"
              @click="proceed"
              data-cy="custom-fields-confirm-button"
            >
              {{ $t("button.confirm") }}
            </SButton>
          </div>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>
<script lang="ts">
import { ModalComponents, ModalStatus } from "@/constants/modal";
import ModalModule from "@/store/modules/modal";
import { useCustomFieldStore } from "@/stores/custom-field.store";
import { useOrderStore } from "@smeetz/order-store";
import {
  mapToCustomFieldAnswerDtos,
  getOrderCustomFieldAnswers,
  mapOrderCustomFieldAnswersToICustomFieldAnswer,
  mapToCreateCustomFieldAnswerDto,
} from "@/utils/vue-helpers/custom-fields.helper";
import {
  createCustomFieldAnswer,
  processOrder,
} from "@/utils/vue-helpers/orders.helper";
import {
  CustomFieldType,
  QuestionType,
  InformationType,
  ICustomFieldAnswer,
  CustomFieldValidation,
} from "@/types/db/product-category";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import MazPhoneNumberInput from "maz-ui/components/MazPhoneNumberInput";
import { parsePhoneNumber } from "libphonenumber-js";
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import CommonModule from "@/store/modules/common";
import { createCompositeId } from "@/utils/helpers/app";
import dayjs from "dayjs";
import { insertFile } from "@/utils/helpers/db";
import binSvg from "@/assets/images/svg/bin-svgrepo-com.svg";
import SmeetzCropper from "./SmeetzCropper.vue";
import { CountryCode } from "@/types/gql/generated/graphql";
import NotificationService from "@/services/notification.service";
import { newOperationContext } from "@/services/package-logger";
import type { OrderOperationContext } from "@smeetz/orders";
import AppModule from "@/store/modules/app";
import {
  SButton,
  SCheckbox,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SInput,
  SLabel,
  SRadioGroup,
  SRadioGroupItem,
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
  STextarea,
} from "@smeetz/ui-core";
import { SDatePicker } from "@smeetz/ui-components/s-date-picker";
import { SCountrySelect } from "@smeetz/ui-components/s-country-select";
import { parseDate } from "@internationalized/date";

enum ValidationDataType {
  Number = 1,
  Email = 2,
}
export default defineComponent({
  name: ModalComponents.CustomFields,
  components: {
    MazPhoneNumberInput,
    SmeetzCropper,
    SButton,
    SCheckbox,
    SCountrySelect,
    SDatePicker,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
    SInput,
    SLabel,
    SRadioGroup,
    SRadioGroupItem,
    SSelect,
    SSelectContent,
    SSelectItem,
    SSelectTrigger,
    SSelectValue,
    STextarea,
  },
  async setup() {
    const { t } = useI18n({});
    const dialogVisible = dialogVisibleForStatus(ModalStatus.CustomFields);
    const isLoading = ref<boolean>(false);
    const canValidate = ref<boolean>(false);
    const selectedFiles: any[] = [];
    const bookingTypeCustomFields = ref<ICustomFieldAnswer[]>([]);
    const ticketTypeCustomFields = ref<ICustomFieldAnswer[]>([]);
    const groupedTicketTypeCustomFieldsByPSS = ref<
      { pssUuid: string; customFields: ICustomFieldAnswer[] }[]
    >([]);
    const priceCategoryData = ref<
      {
        priceId: string;
        priceName: string;
        categoryId: string;
        categoryName: string;
      }[]
    >([]);

    const binIcon = binSvg;
    const binAlt = "bin";
    const binIconStyle = "width: 18px; height: 18px;";

    const orderStore = useOrderStore();

    const displayedCustomFieldsType = ref(CustomFieldType.TICKET);

    const computedDisplayedCustomFieldsType = computed(() => {
      return displayedCustomFieldsType.value;
    });

    const showPriceCategory = computed(() => {
      return (
        priceCategoryData.value &&
        priceCategoryData.value.length &&
        computedDisplayedCustomFieldsType.value === CustomFieldType.TICKET
      );
    });

    const selectedPriceCategory = ref();

    const userCurrentCountry = computed(() => {
      return CommonModule.userCurrentCountry as CountryCode;
    });

    // Helper function to get country code from phone number
    const getCountryCodeFromPhone = (
      phoneNumber: string
    ): string | undefined => {
      if (!phoneNumber) return undefined;
      try {
        const parsed = parsePhoneNumber(phoneNumber);
        return parsed?.country;
      } catch (error) {
        return undefined;
      }
    };

    const initialize = async () => {
      const selectedOrder = orderStore.selectedOrder;

      if (orderStore.customFieldAnswers.length && selectedOrder?.id) {
        const mappedAnswers =
          await mapOrderCustomFieldAnswersToICustomFieldAnswer(
            orderStore.customFieldAnswers
          );

        const existingBookingAnswers = mappedAnswers.filter(
          (answer) =>
            answer.customField.customFieldType === CustomFieldType.BOOKING
        );

        const existingTicketAnswers = mappedAnswers.filter(
          (answer) =>
            answer.customField.customFieldType === CustomFieldType.TICKET
        );

        const { booking, ticket } = await getOrderCustomFieldAnswers(
          selectedOrder.id
        );

        const bookingAnswersByFieldId = new Map(
          existingBookingAnswers.map((answer) => [
            String(answer.customField.id),
            answer,
          ])
        );

        // A single unit item (pssUuid) has one answer per custom field, so the
        // dedup key must combine the pssUuid with the custom field id. Keying on
        // pssUuid alone would drop every field after the first for each ticket.
        const ticketAnswerKey = (answer: ICustomFieldAnswer) =>
          `${String(answer.pssUuid)}_${String(answer.customField.id)}`;

        const ticketAnswerByKey = new Map(
          existingTicketAnswers.map((answer) => [
            ticketAnswerKey(answer),
            answer,
          ])
        );

        const mergedBookingAnswers = [...existingBookingAnswers];
        const mergedTicketAnswers = [...existingTicketAnswers];

        for (const bookingAnswer of booking) {
          const fieldId = String(bookingAnswer.customField.id);

          if (bookingAnswersByFieldId.has(fieldId)) {
            continue;
          }

          mergedBookingAnswers.push(bookingAnswer);
          bookingAnswersByFieldId.set(fieldId, bookingAnswer);
        }

        for (const ticketAnswer of ticket) {
          const key = ticketAnswerKey(ticketAnswer);

          if (ticketAnswerByKey.has(key)) {
            continue;
          }

          mergedTicketAnswers.push(ticketAnswer);
          ticketAnswerByKey.set(key, ticketAnswer);
        }

        bookingTypeCustomFields.value = mergedBookingAnswers;
        ticketTypeCustomFields.value = mergedTicketAnswers;
      } else if (selectedOrder?.id) {
        const { booking, ticket } = await getOrderCustomFieldAnswers(
          selectedOrder.id
        );

        bookingTypeCustomFields.value = booking;
        ticketTypeCustomFields.value = ticket;
      } else {
        bookingTypeCustomFields.value = [];
        ticketTypeCustomFields.value = [];
      }

      const grouped: {
        pssUuid: string;
        customFields: ICustomFieldAnswer[];
      }[] = [];
      const seenPss = new Set<string>();

      for (const customField of ticketTypeCustomFields.value) {
        const key = String(customField.pssUuid);

        if (seenPss.has(key)) {
          continue;
        }

        seenPss.add(key);
        grouped.push({
          pssUuid: key,
          customFields: ticketTypeCustomFields.value.filter(
            (candidate) => candidate.pssUuid === customField.pssUuid
          ),
        });
      }

      groupedTicketTypeCustomFieldsByPSS.value = grouped;

      priceCategoryData.value = [];
      if (orderStore.selectedOrder) {
        const orderLinesWithCustomFields = orderStore.allOrderLines.filter(
          (line) =>
            line.unitItems?.some((unitItem) =>
              ticketTypeCustomFields.value.some(
                (cf) => cf.pssUuid === unitItem.id
              )
            )
        );

        const seenKeys = new Set<string>();

        for (const line of orderLinesWithCustomFields) {
          const priceId = line.priceId;
          const categoryId = line.categoryId;
          const key = `${categoryId}_${priceId}`;

          if (seenKeys.has(key)) {
            continue;
          }

          seenKeys.add(key);
          priceCategoryData.value.push({
            priceId,
            priceName: line.priceName,
            categoryId,
            categoryName: line.categoryName,
          });
        }
      }

      if (priceCategoryData.value.length) {
        selectedPriceCategory.value = priceCategoryData.value[0];
      } else {
        selectedPriceCategory.value = undefined;
      }

      displayedCustomFieldsType.value = bookingTypeCustomFields.value.length
        ? CustomFieldType.BOOKING
        : CustomFieldType.TICKET;
    };

    initialize().catch((error) => {
      console.error("Failed to initialize custom fields modal", error);
    });

    /**
     * User can confirm only if all required fields have been answered
     */
    const canConfirm = computed(() => {
      for (let i = 0; i < bookingTypeCustomFields.value.length; i++) {
        // check if:
        // - current field is mandatory AND
        // we have no "answer" and current field isn't a list OR
        // we have no listAnswer and current field is a list
        if (
          bookingTypeCustomFields.value[i].customField.validation ===
          CustomFieldValidation.MANDATORY
        ) {
          if (
            (!bookingTypeCustomFields.value[i].listAnswer ||
              !bookingTypeCustomFields.value[i].listAnswer.length) &&
            bookingTypeCustomFields.value[i].customField.questionType ===
              QuestionType.LIST
          ) {
            // This a LIST or a DROPDOWN and it doesn't have a listAnswer
            return false;
          }
          if (
            !bookingTypeCustomFields.value[i].answer &&
            [QuestionType.LIST].findIndex(
              (it) =>
                it === bookingTypeCustomFields.value[i].customField.questionType
            ) === -1
          ) {
            // This is not a LIST nor a DROPDOWN and it dosn't have an answer
            return false;
          }
        }
      }
      for (let i = 0; i < ticketTypeCustomFields.value.length; i++) {
        // check if:
        // - current field is mandatory AND
        // we have no "answer" and current field isn't a list OR
        // we have no listAnswer and current field is a list
        if (
          ticketTypeCustomFields.value[i].customField.validation ===
          CustomFieldValidation.MANDATORY
        ) {
          if (
            (!ticketTypeCustomFields.value[i].listAnswer ||
              !ticketTypeCustomFields.value[i].listAnswer.length) &&
            ticketTypeCustomFields.value[i].customField.questionType ===
              QuestionType.LIST
          ) {
            // This a LIST or a DROPDOWN and it doesn't have a listAnswer
            return false;
          }
          if (
            !ticketTypeCustomFields.value[i].answer &&
            [QuestionType.LIST].findIndex(
              (it) =>
                it === ticketTypeCustomFields.value[i].customField.questionType
            ) === -1
          ) {
            // This is not a LIST nor a DROPDOWN and it dosn't have an answer
            return false;
          }
        }
      }
      return true;
    });

    function isEmailValid(custAnswer: ICustomFieldAnswer) {
      const validationResult = { isValid: true, message: "" };
      if (custAnswer.customField.informationType === InformationType.EMAIL) {
        // Lets validate the email using same regew used by vuelidate
        // https://github.com/vuelidate/vuelidate/blob/%40vuelidate/validators%402.0.3/packages/validators/src/raw/email.js

        const emailRegex =
          // eslint-disable-next-line no-control-regex
          /^(?:[A-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9]{2,}(?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;

        let res = emailRegex.test(custAnswer.answer);
        if (res) {
          validationResult.isValid = true;
          validationResult.message = "";
          return validationResult;
        } else {
          validationResult.isValid = false;
          validationResult.message = "Email not valid";
          return validationResult;
        }
      }
      return validationResult;
    }
    function selectBookingTypeCustomFields() {
      displayedCustomFieldsType.value = CustomFieldType.BOOKING;
    }
    function selectTicketTypeCustomFields() {
      displayedCustomFieldsType.value = CustomFieldType.TICKET;
      selectedPriceCategory.value = priceCategoryData.value[0];
    }

    function languagesList(customFieldList: string[]) {
      const languages = CommonModule.languages;
      const filteredLanguages: { key: string; value: string }[] = [];
      languages
        .filter((lang) => customFieldList.includes(lang.code))
        .forEach((lang) => {
          filteredLanguages.push({
            key: lang.code,
            value: lang.name,
          });
        });
      return filteredLanguages;
    }

    function selectPriceCategory(priceCategory: {
      priceId: string;
      priceName: string;
      categoryId: string;
      categoryName: string;
    }) {
      selectedPriceCategory.value = priceCategory;
    }

    function handleFileUpload(
      event: any,
      customFieldUuid: string,
      type: CustomFieldType
    ) {
      const file = event.target.files[0];

      // Now lets fill the file name into 'answer' field based on whether we are in Ticket custom fields or Booking custom fields
      if (type === CustomFieldType.BOOKING) {
        const fileField = bookingTypeCustomFields.value.find(
          (c) => c.uuid === customFieldUuid
        );

        if (fileField) {
          fileField.isInvalid = false;
          fileField.validationMessage = "";
          const fileSize = file.size;
          if (fileSize > 10485760) {
            fileField.isInvalid = true;
            fileField.validationMessage = "File should be less than 10 mb";
            return;
          }
          fileField.answer = file.name;
          fileField.fileID = createCompositeId([
            dayjs().toISOString(),
            file.name,
          ]);

          const reader = new FileReader();
          reader.onload = async (ev: any) => {
            const blob = new Blob([file], { type: file.type });
            // Lets keep a url for the image so that it can be displayed
            fileField.fileUrl = URL.createObjectURL(blob);

            selectedFiles.push({
              id: fileField.fileID,
              file: blob,
            });
          };
          reader.readAsBinaryString(file);
        }
      }
      if (type === CustomFieldType.TICKET) {
        const fileField = groupedTicketTypeCustomFieldsByPSS.value
          .map((g) => g.customFields)
          .flat()
          .find((c) => c.uuid === customFieldUuid);

        if (fileField) {
          fileField.isInvalid = false;
          fileField.validationMessage = "";
          const fileSize = file.size;
          if (fileSize > 10485760) {
            fileField.isInvalid = true;
            fileField.validationMessage = "File should be less than 10 mb";
            return;
          }
          fileField.answer = file.name;
          fileField.fileID = createCompositeId([
            dayjs().toISOString(),
            file.name,
          ]);

          const reader = new FileReader();
          reader.onload = async (ev: any) => {
            const blob = new Blob([file], { type: file.type });
            // Lets keep a url for the image so that it can be displayed
            fileField.fileUrl = URL.createObjectURL(blob);

            selectedFiles.push({
              id: fileField.fileID,
              file: blob,
            });
          };
          reader.readAsBinaryString(file);
        }
      }
    }

    function removeFile(cust: ICustomFieldAnswer) {
      cust.fileUrl = "";
      cust.fileID = "";
      cust.answer = "";
      cust.isCropped = false;
    }

    function confirmCroppedPicture(cust: ICustomFieldAnswer) {
      console.log("this.$refs.cropperRef");
      console.log(this.$refs[cust.uuid + "_cropper"]);
      console.log("this.$refs[cust.uuid+'_cropper'][0]");
      console.log(this.$refs[cust.uuid + "_cropper"][0]);
      ////
      // THIS IS BASE 64 IMAGE
      // const croppedData = this.$refs.cropperRef[0].getCroppedCanvas().toDataURL();
      // console.log("croppedData");
      // console.log(croppedData);
      ////
      // THIS IS BASE 64 IMAGE
      // console.log("going to export the image");
      // const img = (this.$refs.cropperRef[0] as any).getCroppedCanvas().toDataURL('image/jpeg');
      // console.log("done exporting the image");
      // console.log("img");
      // console.log(img);
      ////

      // Get the cropped file as blob, And push it to selectedFiles array, And display it to the user, And destroy cropper
      (this.$refs[cust.uuid + "_cropper"][0] as any)
        .getCroppedCanvas()
        .toBlob((blob: any) => {
          const cropedFile: any = new File(
            [blob],
            String(cust.fileID + "hola")
          );
          // const fileField = bookingTypeCustomFields.value.find(
          //   (c) => c.uuid === cust.uuid
          // );
          const fileField = cust;

          if (fileField) {
            fileField.fileID = createCompositeId([dayjs().toISOString()]);
            fileField.answer = fileField.fileID;
            // Lets keep a url for the image so that it can be displayed
            fileField.fileUrl = URL.createObjectURL(blob);
            selectedFiles.push({
              id: fileField.fileID,
              file: cropedFile,
            });
            cust.fileUrl = fileField.fileUrl;
            cust.isCropped = true;
          }
        }, "image/jpeg");
      // Lets now destroy the cropper
      (this.$refs[cust.uuid + "_cropper"][0] as any).destroy();
      // Hide cropping are and keep only the img element displaying the cropped image
      //cust.uuid+'_cropper'
      const cropperParent = this.$refs[cust.uuid + "_cropper_parent"][0];
      // hiding cropper parent
      cropperParent.style.display = "none";
      ////
    }

    function cancelCroppedPicture(cust: ICustomFieldAnswer) {
      cust.answer = "";
      cust.fileID = "";
      cust.fileUrl = "";
    }

    function stripHtml(val: string): string {
      return val.replace(/<[^>]+>/g, "").trim();
    }

    // A stored answer can hold a value the picker cannot parse, and parseDate throws
    // during render, which leaves the dialog empty behind its overlay (L3-8180)
    function dateAnswerValue(answer: string) {
      try {
        return answer ? parseDate(answer) : undefined;
      } catch {
        return undefined;
      }
    }

    const appLanguage = computed(() => AppModule.language);

    // Filter grouped custom fields by selected price category
    const filteredGroupedCustomFields = computed(() => {
      if (!selectedPriceCategory.value || !orderStore.allOrderLines.length) {
        return [];
      }

      return groupedTicketTypeCustomFieldsByPSS.value.filter(
        (groupedCustomField) => {
          // Find the order line that has a unitItem matching this pssUuid
          const orderLine = orderStore.allOrderLines.find((line) =>
            line.unitItems?.some(
              (unitItem) => unitItem.id === groupedCustomField.pssUuid
            )
          );

          // Check if this order line matches the selected price category
          return (
            orderLine &&
            orderLine.priceId === selectedPriceCategory.value.priceId &&
            orderLine.categoryId === selectedPriceCategory.value.categoryId
          );
        }
      );
    });

    return {
      t,
      isLoading,
      dialogVisible,
      bookingTypeCustomFields,
      ticketTypeCustomFields,
      selectBookingTypeCustomFields,
      selectTicketTypeCustomFields,
      QuestionType,
      InformationType,
      languagesList,
      userCurrentCountry,
      canConfirm,
      CustomFieldValidation,
      displayedCustomFieldsType,
      computedDisplayedCustomFieldsType,
      CustomFieldType,
      priceCategoryData,
      selectedPriceCategory,
      showPriceCategory,
      selectPriceCategory,
      groupedTicketTypeCustomFieldsByPSS,
      handleFileUpload,
      selectedFiles,
      binIcon,
      binAlt,
      binIconStyle,
      removeFile,
      ValidationDataType,
      isEmailValid,
      canValidate,
      confirmCroppedPicture,
      cancelCroppedPicture,
      appLanguage,
      getCountryCodeFromPhone,
      stripHtml,
      filteredGroupedCustomFields,
      dateAnswerValue,
    };
  },
  methods: {
    // Multi-select list answers are stored as an array on cust.listAnswer.
    // the design-system checkbox is a single boolean, so membership is managed here.
    isListChecked(cust: ICustomFieldAnswer, item: string): boolean {
      return Array.isArray(cust.listAnswer) && cust.listAnswer.includes(item);
    },
    toggleListAnswer(
      cust: ICustomFieldAnswer,
      item: string,
      checked: boolean | "indeterminate"
    ) {
      if (!Array.isArray(cust.listAnswer)) cust.listAnswer = [];
      if (checked === true) {
        if (!cust.listAnswer.includes(item)) cust.listAnswer.push(item);
      } else {
        cust.listAnswer = cust.listAnswer.filter((x: string) => x !== item);
      }
    },
    hideDialog() {
      ModalModule.setStatus(ModalStatus.Hidden);
    },
    async proceed() {
      // If We are now displaying Booking custom fields, if there are Ticket custom fields, then we need to display tickets custom fields
      // If we are now displaying Ticket custom fields, if we are now displaying the last category custom fields, then we need to call proceedAnswers
      // If we are now displaying Ticket custom fields but we are not displaying the last category custom fields, then we need to select the next category
      if (this.displayedCustomFieldsType === CustomFieldType.BOOKING) {
        // If we have Tickets custom fields then we show them, otherwise we start processing
        if (this.ticketTypeCustomFields && this.ticketTypeCustomFields.length) {
          this.selectTicketTypeCustomFields();
        } else {
          try {
            await this.proceedAnswers();
          } catch {
            // Journaled + surfaced by the order helpers; the modal stays
            // open so the cashier can retry.
          }
        }
      } else if (this.displayedCustomFieldsType === CustomFieldType.TICKET) {
        // If we are displaying the last category then we will start processing, otherwise we display the next category
        const selectedPriceCat = this.selectedPriceCategory;
        const index = this.priceCategoryData.findIndex(
          (p: any) =>
            p.priceId === selectedPriceCat.priceId &&
            p.categoryId === selectedPriceCat.categoryId
        );
        if (index > -1) {
          if (index === this.priceCategoryData.length - 1) {
            // So we are actually displaying the last priceCategory, lets start processing
            try {
              await this.proceedAnswers();
            } catch {
              // Journaled + surfaced by the order helpers; the modal stays
              // open so the cashier can retry.
            }
          } else {
            // We are not displaying the last price category, lets select the next one
            const nextPriceCat = this.priceCategoryData[index + 1];
            this.selectPriceCategory(nextPriceCat);
          }
        }
      }
    },
    /**
     * Validate all custom field answers
     * Using new order custom field validateOrderCustomFields API
     *
     * @returns {Promise<boolean>}
     */
    async validateAnswers(): Promise<boolean> {
      const customFieldStore = useCustomFieldStore();
      const orderStore = useOrderStore();
      const selectedOrder = orderStore.selectedOrder?.id;

      if (!selectedOrder) return false;

      const allCustomFields = [
        ...this.bookingTypeCustomFields,
        ...this.ticketTypeCustomFields,
      ];

      const answerDtos = mapToCustomFieldAnswerDtos(allCustomFields);
      const validationResult = await customFieldStore.validateOrderCustomFields(
        selectedOrder,
        answerDtos
      );

      if (!validationResult.isValid) {
        // Show error messages for invalid fields
        if (validationResult.fields && validationResult.fields.length > 0) {
          const errorMessages = validationResult.fields
            .filter((field: any) => !field.isValid && field.errorMessage)
            .map((field: any) => `${field.fieldLabel}: ${field.errorMessage}`)
            .join("\n");

          if (errorMessages) {
            NotificationService.showCustomError(errorMessages);
          }
        }

        return false;
      }

      return validationResult.isValid;
    },
    async proceedAnswers() {
      console.log("----------- hi from proceedAnswers");
      this.canValidate = true;
      if (!this.canConfirm) {
        return;
      }

      const isValid = await this.validateAnswers();

      if (!isValid) return;

      // The whole confirm press is one user action: saving the answers and
      // the processing that follows trace under a single requestId.
      const ctx = newOperationContext();
      await this.confirmAnswers(ctx);

      await processOrder(ctx);
    },
    async confirmAnswers(ctx: OrderOperationContext = newOperationContext()) {
      const customFieldStore = useCustomFieldStore();
      const orderStore = useOrderStore();
      const selectedOrder = orderStore.selectedOrder?.id;

      if (!selectedOrder) return;

      // Combine booking and ticket type custom fields for validation
      const allCustomFields = [
        ...this.bookingTypeCustomFields,
        ...this.ticketTypeCustomFields,
      ];

      // Convert to DTO format and validate
      const answerDtos = mapToCustomFieldAnswerDtos(allCustomFields);
      const validationResult = await customFieldStore.validateOrderCustomFields(
        selectedOrder,
        answerDtos
      );

      if (!validationResult.isValid) {
        NotificationService.showCustomError(String(validationResult));

        return;
      }

      // Process each custom field answer
      for (const [index, customFieldAnswer] of allCustomFields.entries()) {
        const answerDto = answerDtos[index];

        // Check if we already have this custom field answer in the order using the unique uuid/id
        const existingAnswer = orderStore.customFieldAnswers.find(
          (field) => field.id === customFieldAnswer.uuid
        );

        if (
          customFieldAnswer.customField.questionType === QuestionType.FILE &&
          !existingAnswer &&
          customFieldAnswer.fileID
        ) {
          const file = this.selectedFiles.find(
            (f: any) => f.id === customFieldAnswer.fileID
          ).file;

          await insertFile({
            _id: customFieldAnswer.fileID,
            file: file,
            customFieldId: customFieldAnswer.customField.id,
          });
        }

        if (this.isPreviousOrderSameAnswer(customFieldAnswer)) {
          console.log(
            "IS PREVIOUS ORDER SAME ANSWER, SKIPPING",
            customFieldAnswer.answer
          );
          continue;
        } else if (existingAnswer) {
          const updateCustomField =
            mapToCreateCustomFieldAnswerDto(customFieldAnswer);

          createCustomFieldAnswer(updateCustomField, ctx);
        } else {
          const createAnswerDto =
            mapToCreateCustomFieldAnswerDto(customFieldAnswer);

          createCustomFieldAnswer(createAnswerDto, ctx);
        }
      }
    },
    isPreviousOrderSameAnswer(customFieldAnswer: ICustomFieldAnswer): boolean {
      const orderStore = useOrderStore();
      const sessions = orderStore.selectedOrder?.previousSessions ?? [];

      for (const session of sessions) {
        if (!Array.isArray(session.customFieldAnswers)) {
          continue;
        }

        const sameAnswer = session.customFieldAnswers.find(
          (answer: any) => answer.id === customFieldAnswer.uuid
        );

        if (sameAnswer) {
          if (sameAnswer.answer === customFieldAnswer.answer) {
            return true;
          }
        }
      }

      return false;
    },
  },
});
</script>
<style lang="scss">
.confirm-button-container {
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
